import update from 'immutability-helper'
import * as actions from '../actions/settings'
import * as settingsConstants from '../constants/settings'

const INITIAL_STATE = {
  // User Generated Content and Salesforce are hidden until those features are
  // rebuilt. Their forms remain in SettingsUtils, and PrivateApp redirects
  // their old routes to /settings.
  settingsMenu: [
    {
      id: 'mst',
      name: 'App Settings',
      items: [
        {
          id: 'branding',
          icon: 'certificate',
          route: '/settings/mst/branding',
          displayName: 'Branding',
        },
        {
          id: 'featured',
          icon: 'rocket',
          route: '/settings/mst/featured',
          displayName: 'Featured Assets',
        },
        {
          id: 'menu',
          icon: 'list',
          route: '/settings/mst/menu',
          displayName: 'Menu',
        },
        {
          id: 'resources',
          icon: 'info-circle',
          route: '/settings/mst/resources',
          displayName: 'Resources',
        },
      ],
    },
    {
      id: 'dam',
      name: 'DAM Settings',
      items: [
        {
          id: 'general',
          icon: 'sliders',
          route: '/settings/dam/general',
          displayName: 'General',
        },
        {
          id: 'builders',
          icon: 'puzzle-piece',
          route: '/settings/dam/builders',
          displayName: 'Builders',
        },
        {
          id: 'profiles',
          icon: 'address-card-o',
          route: '/settings/dam/profiles',
          displayName: 'Customer Profiles',
        },
        {
          id: 'shared-endpoints',
          icon: 'share-alt',
          route: '/settings/dam/shared-endpoints',
          displayName: 'Shared Endpoints',
          requiresSharedEndpointsProvider: true,
        },
      ],
    },
    {
      id: 'permissions',
      name: 'Permissions',
      items: [
        {
          id: 'invitations',
          icon: 'id-card',
          route: '/settings/permissions/invitations',
          displayName: 'Invitations',
        },
        {
          id: 'users',
          icon: 'user',
          route: '/settings/permissions/users',
          displayName: 'Users',
        },
        {
          id: 'groups',
          icon: 'users',
          route: '/settings/permissions/groups',
          displayName: 'Groups',
        },
        {
          id: 'roles',
          icon: 'key',
          route: '/settings/permissions/roles',
          displayName: 'Privileges',
        },
        {
          id: 'approval',
          icon: 'user-plus',
          route: '/settings/permissions/approval',
          displayName: 'Registrations',
        },
      ],
    },
  ],
  settingsOpenKeys: ['mst'],
  settingsActiveKey: 'mst',
  settingsActiveItem: 'branding',
  newBrandingLogo: null,
  newLogoValidationMsg: null,
  newBrandingSplashScreen: null,
  newSplashScreenValidationMsg: null,
  brandingMessage: null,
  brandingMessageType: null,
  branding: {
    logo: null,
    splashScreen: null,
  },
  featured: {
    carousel: [],
  },
  menu: {
    modules: {
      dashboard: 'Dashboard',
      library: 'Library',
      profile: 'My Profile',
      customerProfiles: 'Customer Profiles',
      news: 'News',
      navigation: 'Visual Navigation',
      comparison: 'Comparison',
      resources: 'Resources',
      calculators: 'Calculators',
      upload: 'Upload',
      search: 'Search',
      shareQueue: 'Share Queue',
      refresh: 'Refresh Content',
      support: 'Support',
    },
    custom: [],
    // Ids of the standard/custom menu items, in the order the app shows them,
    // and the ids the app suppresses. Empty means "whatever the app ships
    // with" -- see resolveMenuOrder in dux/constants/menu.
    order: [],
    hidden: [],
  },
  resources: {
    custom: [],
    social: {
      facebook: '',
      twitter: '',
      instagram: '',
      youTube: '',
      linkedIn: '',
    },
  },
  modules: {
    news: {
      enabled: true,
    },
    navigation: {
      enabled: false,
    },
    comparison: {
      enabled: false,
    },
    calculators: {
      enabled: false,
    },
    formAnalytics: {},
  },
  uploads: {
    enabled: false,
    groups: [],
    maxSize: 52428800,
    metadata: {
      description: {
        defaultValue: null,
        required: false,
      },
      tags: {
        defaultValue: null,
        required: false,
      },
      title: {
        defaultValue: null,
        required: false,
      },
    },
  },
  integrations: {
    salesforce: {
      displayValue: 'Salesforce',
      enabled: false,
      client: 'test',
      defaultMessage: null,
      defaultSubject: 'tests',
    },
  },
  builders: {
    comparisons: {
      allowCustomDisclaimer: false,
      globalDisclaimer: '',
    },
  },
  profiles: {},
  general: {},
}

/*
 * Merge a stored setting over its default shape.
 *
 * Settings arrive from the API as whole objects and used to be assigned
 * wholesale, which meant any key the stored row happened to omit silently
 * erased the default. That is not cosmetic: popart's stored `uploads` row has
 * no `groups`, so `state.settings.uploads.groups` became undefined and
 * virtualizeUploadGroups threw on `.map` — which, because it never reached its
 * SET_VIRTUAL_FOLDERS dispatch, deadlocked refreshStateSaga and hung every
 * content mutation in the DAM.
 *
 * Rules:
 *  - plain objects merge key by key, stored value winning
 *  - arrays and primitives REPLACE, never merge — an emptied carousel or a
 *    removed upload group has to be able to win over a non-empty default
 *  - an explicit null in the stored value is respected, not filled in
 */
const isPlainObject = (value) =>
  !!value && typeof value === 'object' && !Array.isArray(value)

export const mergeOverDefault = (defaultValue, storedValue) => {
  if (storedValue === undefined) return defaultValue
  if (!isPlainObject(defaultValue) || !isPlainObject(storedValue)) {
    return storedValue
  }

  const merged = { ...defaultValue }

  Object.keys(storedValue).forEach((key) => {
    merged[key] = mergeOverDefault(defaultValue[key], storedValue[key])
  })

  return merged
}

const withSettingDefaults = (setting, value) =>
  mergeOverDefault(INITIAL_STATE[setting], value)

export default function settingsReducer(state = INITIAL_STATE, action) {
  let index = -1
  switch (action.type) {
    case actions.SET_SETTINGS_OPEN_KEYS:
      return {
        ...state,
        settingsOpenKeys: action.keys,
      }

    case actions.SET_SETTINGS_ACTIVE_KEY:
      return {
        ...state,
        settingsActiveKey: action.key,
      }

    case actions.SET_SETTINGS_ACTIVE_ITEM:
      return {
        ...state,
        settingsActiveItem: action.item,
      }

    case actions.SET_BRANDING_MESSAGE:
      return {
        ...state,
        brandingMessage: action.msg,
        brandingMessageType: action.msgType,
      }

    case actions.UPDATE_SETTINGS:
      return {
        ...state,
        [action.result.setting]: withSettingDefaults(
          action.result.setting,
          action.result.value,
        ),
      }

    case actions.ADD_ASSET_TO_CAROUSEL:
      return update(state, {
        featured: {
          carousel: {
            $push: [
              {
                asset: action.asset,
                folder: action.folder,
                displayText: action.displayText,
              },
            ],
          },
        },
      })

    case actions.REMOVE_ASSET_FROM_CAROUSEL:
      index = state.featured.carousel.findIndex(
        (obj) =>
          obj.asset === action.asset.asset &&
          obj.folder === action.asset.folder,
      )
      return update(state, {
        featured: {
          carousel: {
            $splice: [[index, 1]],
          },
        },
      })

    case actions.REPLACE_CAROUSEL_ASSET:
      index = state.featured.carousel.findIndex(
        (obj) =>
          obj.asset === action.originalAsset.asset &&
          obj.folder === action.originalAsset.folder,
      )
      return update(state, {
        featured: {
          carousel: {
            $splice: [
              [
                index,
                1,
                {
                  asset: action.asset,
                  folder: action.folder,
                  displayText: action.displayText,
                },
              ],
            ],
          },
        },
      })

    case actions.REORDER_CAROUSEL: {
      const carousel = state.featured.carousel.slice()
      const item = carousel.splice(action.fromIndex, 1)[0]
      carousel.splice(action.toIndex, 0, item)
      return {
        ...state,
        featured: {
          ...state.featured,
          carousel,
        },
      }
    }

    case actions.RESET_CAROUSEL:
      return {
        ...state,
        featured: {
          carousel: action.carousel,
        },
      }

    case actions.SAVE_CUSTOM_MENU_RESOURCE:
      return update(state, {
        menu: {
          custom: {
            $apply: (x) =>
              x.find((resource) => resource.id === action.resource.id)
                ? x.map((resource) =>
                    resource.id !== action.resource.id
                      ? resource
                      : action.resource,
                  )
                : x.concat(action.resource),
          },
        },
      })

    case actions.REMOVE_CUSTOM_MENU_RESOURCE:
      return update(state, {
        menu: {
          custom: {
            $apply: (x) =>
              x.filter((resource) => resource.id !== action.resource.id),
          },
        },
      })

    case actions.RESET_MENU_SETTINGS:
      return update(state, {
        menu: {
          custom: { $set: action.original.custom },
          order: { $set: action.original.order },
          hidden: { $set: action.original.hidden },
        },
      })

    case actions.SET_MENU_ORDER:
      return update(state, {
        menu: {
          order: { $set: action.order },
        },
      })

    case actions.TOGGLE_MENU_ITEM_HIDDEN:
      return update(state, {
        menu: {
          hidden: {
            $apply: (hidden) => {
              const current = Array.isArray(hidden) ? hidden : []
              return current.indexOf(action.id) === -1
                ? current.concat(action.id)
                : current.filter((id) => id !== action.id)
            },
          },
        },
      })

    case actions.SET_CUSTOM_RESOURCE:
      return update(state, {
        resources: {
          custom: {
            $apply: (x) =>
              x.find((el) => el.id === action.resource.id)
                ? x.map((el) =>
                    el.id === action.resource.id ? action.resource : el,
                  )
                : x.concat(action.resource),
          },
        },
      })

    case actions.REMOVE_CUSTOM_RESOURCE:
      return update(state, {
        resources: {
          custom: {
            $apply: (x) =>
              x.filter((resource) => resource.id !== action.resourceId),
          },
        },
      })

    case actions.CANCEL_RESOURCES_UPDATE:
      return update(state, {
        resources: {
          custom: {
            $set: action.originalResources,
          },
          social: {
            $set: state.resources.social,
          },
        },
      })

    case actions.SAVE_SETTINGS_SUCCESS:
      return update(state, {
        [action.setting]: {
          $set: withSettingDefaults(action.setting, action.value),
        },
      })

    default:
      return state
  }
}
